import { useListWorkerVersions } from "#src/components/hooks/adapters/useWorkers";
import {
  useMemoizedFormState,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import {
  Form,
  Panel,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  DEFAULT_WORKER_MEMORY_MB,
  DEFAULT_WORKER_TIMEOUT_SECONDS,
  MAX_WORKER_MEMORY_MB,
  MAX_WORKER_TIMEOUT_SECONDS,
  MIN_WORKER_MEMORY_MB,
  SortDirection,
  type CreateWorkerVersionComputeConfigRequestType,
} from "@validereinc/domain";
import { resolveWorkerVersionComputeConfig } from "@validereinc/domain-controllers";
import {
  validateNumericBounds,
  validateRequiredInteger,
} from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useMemo } from "react";
import styles from "./CreateWorkerVersionFormPanels.module.scss";

const cx = classNames.bind(styles);

type ComputeConfigFormValues = {
  compute_config: CreateWorkerVersionComputeConfigRequestType;
};

const WorkerVersionComputeConfigFormStep = ({
  workerId,
  stepNumber,
}: {
  workerId: string;
  stepNumber: number;
}) => {
  const { currentStep, updateStep } = useMultiStepFormContext();

  const { data: workerVersionsData, isLoading: isLoadingWorkerVersions } =
    useListWorkerVersions(
      {
        meta: { id: workerId },
        filters: {},
        sortBy: "version",
        sortDirection: SortDirection.DESCENDING,
      },
      { enabled: Boolean(workerId) }
    );

  const latestWorkerVersion = useMemo(() => {
    const versions = workerVersionsData?.data ?? [];
    return versions[0];
  }, [workerVersionsData?.data]);

  const formValues = useMemo(() => {
    if (!latestWorkerVersion) {
      return {
        compute_config: {
          timeout_seconds: DEFAULT_WORKER_TIMEOUT_SECONDS,
          memory_mb: DEFAULT_WORKER_MEMORY_MB,
        },
      };
    }

    const { timeoutSeconds, memoryMb } = resolveWorkerVersionComputeConfig(
      latestWorkerVersion.compute_config
    );

    return {
      compute_config: {
        timeout_seconds: timeoutSeconds,
        memory_mb: memoryMb,
      },
    };
  }, [latestWorkerVersion]);

  // Prefer `values` over defaultValues: common-components useForm resets to
  // defaultValues on mount, which would overwrite prepopulated values when the
  // worker-versions query is already cached.
  const form = useForm<ComputeConfigFormValues>({
    mode: "onChange",
    values: formValues,
    resetOptions: {
      keepDirtyValues: true,
    },
  });

  const getFormState = useMemoizedFormState(form);

  const isPanelLoaded = !isLoadingWorkerVersions;

  useEffect(() => {
    if (!isPanelLoaded || currentStep !== stepNumber) {
      return;
    }

    void form.trigger();
  }, [currentStep, isPanelLoaded, stepNumber]);

  useEffect(() => {
    updateStep(stepNumber, {
      getValues: () => ({ compute_config: form.getValues().compute_config }),
      getSubmitHandler: (setValues) => () => {
        setValues({ compute_config: form.getValues().compute_config });
        return Promise.resolve();
      },
      getFormState,
    });
  }, [stepNumber, getFormState]);

  if (currentStep !== stepNumber) {
    return null;
  }

  return (
    <Panel
      className={cx("panel")}
      loaded={isPanelLoaded}
    >
      <Form
        {...form}
        className={cx("form")}
      >
        <h4 className={cx("section-title")}>Configuration</h4>
        <TextInput
          name="compute_config.timeout_seconds"
          label="Timeout"
          type="number"
          unit="sec"
          isRequired
          rules={{
            validate: {
              requiredAndInteger: (value) =>
                validateRequiredInteger(value, "Timeout"),
              bounds: (value) => {
                const requiredResult = validateRequiredInteger(
                  value,
                  "Timeout"
                );
                if (requiredResult !== true) {
                  return true;
                }

                return validateNumericBounds(
                  Number(value),
                  { min: 1, max: MAX_WORKER_TIMEOUT_SECONDS },
                  "Timeout"
                );
              },
            },
          }}
        />
        <TextInput
          name="compute_config.memory_mb"
          label="Memory"
          type="number"
          unit="MB"
          isRequired
          rules={{
            validate: {
              requiredAndInteger: (value) =>
                validateRequiredInteger(value, "Memory"),
              bounds: (value) => {
                const requiredResult = validateRequiredInteger(value, "Memory");
                if (requiredResult !== true) {
                  return true;
                }

                return validateNumericBounds(
                  Number(value),
                  { min: MIN_WORKER_MEMORY_MB, max: MAX_WORKER_MEMORY_MB },
                  "Memory"
                );
              },
            },
          }}
        />
      </Form>
    </Panel>
  );
};

export default WorkerVersionComputeConfigFormStep;
